import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Activity, TrendingUp, TrendingDown, Clock, AlertCircle, ExternalLink } from 'lucide-react';
import { dashboardApi } from '@/api/dashboard';
import type { AgentDecision, Fill, Holding, PendingOrder } from '@/api/types';
import { num, pct, pnlColor, timeOnly, ago } from '@/lib/format';
import { cn } from '@/lib/cn';
import { AgentAvatar, displayAgent } from '@/components/AgentAvatar';

export function LiveConsole() {
  const console_ = useQuery({
    queryKey: ['liveConsole'],
    queryFn: dashboardApi.liveConsole,
    refetchInterval: 10_000,
  });
  const holdings = useQuery({
    queryKey: ['holdings'],
    queryFn: dashboardApi.holdings,
    refetchInterval: 30_000,
  });

  const movers = (holdings.data ?? [])
    .filter((h) => h.unrealizedPnlPct != null)
    .sort((a, b) => Math.abs(b.unrealizedPnlPct) - Math.abs(a.unrealizedPnlPct))
    .slice(0, 6);

  return (
    <div className="p-4 lg:p-6">
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-[10px] bg-surface-2 grid place-items-center text-text-dim">
          <Activity size={20} />
        </div>
        <div className="flex-1">
          <h1 className="text-xl font-bold">Live Console</h1>
          <div className="text-xs text-text-faint">
            {console_.data
              ? `updated ${ago(console_.data.asOf)}`
              : console_.isLoading ? 'loading…' : 'unavailable'}
          </div>
        </div>
      </div>

      {console_.isError && (
        <div className="card flex items-center gap-3 text-kam-red mb-4">
          <AlertCircle size={18} />
          <span>Couldn't load /api/console/today</span>
        </div>
      )}

      <div className="grid gap-4 lg:grid-cols-2 max-w-7xl">
        <MoversCard movers={movers} loading={holdings.isLoading} />
        <FillsCard fills={console_.data?.fills ?? []} loading={console_.isLoading} />
        <PendingCard pending={console_.data?.pending ?? []} loading={console_.isLoading} />
        <DecisionsCard decisions={console_.data?.decisions ?? []} loading={console_.isLoading} />
      </div>
    </div>
  );
}

// ─── Movers ──────────────────────────────────────────────────────────
function MoversCard({ movers, loading }: { movers: Holding[]; loading: boolean }) {
  return (
    <Section
      title="Movers today"
      subtitle={`${movers.length} of ${movers.length} positions`}
      viewAllHref="/holdings"
    >
      {loading && <Empty>Loading…</Empty>}
      {!loading && movers.length === 0 && <Empty>No positions held.</Empty>}
      {movers.length > 0 && (
        <div className="flex flex-col">
          {movers.map((h) => (
            <div key={h.code} className="row flex items-center gap-3 py-2.5 border-b border-[color:var(--border)] last:border-0">
              <div className={cn(
                'w-9 h-9 rounded-[10px] grid place-items-center flex-none',
                h.unrealizedPnlPct >= 0 ? 'bg-kam-green-soft text-kam-green' : 'bg-kam-red-soft text-kam-red',
              )}>
                {h.unrealizedPnlPct >= 0 ? <TrendingUp size={16} /> : <TrendingDown size={16} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold truncate">{h.name}</div>
                <div className="text-[11px] text-text-faint font-mono">{h.code} · {h.quantity}sh</div>
              </div>
              <div className="text-right">
                <div className="font-mono text-sm">{num(h.currentPrice)}</div>
                <div className={cn('font-mono text-[11px]', pnlColor(h.unrealizedPnlPct))}>
                  {pct(h.unrealizedPnlPct)}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </Section>
  );
}

// ─── Fills ───────────────────────────────────────────────────────────
function FillsCard({ fills, loading }: { fills: Fill[]; loading: boolean }) {
  return (
    <Section title="Today's fills" subtitle={`${fills.length} fill${fills.length === 1 ? '' : 's'}`}>
      {loading && <Empty>Loading…</Empty>}
      {!loading && fills.length === 0 && <Empty>No fills today.</Empty>}
      {fills.map((f) => (
        <div key={f.requestId} className="row flex items-center gap-3 py-2.5 border-b border-[color:var(--border)] last:border-0">
          <div className="text-[10px] font-mono text-text-faint w-14 flex-none">
            {f.filledAt ? timeOnly(f.filledAt) : '—'}
          </div>
          <span className={cn(
            'inline-flex items-center px-1.5 py-0.5 rounded-chip text-[9px] font-bold border',
            f.side === 'BUY'
              ? 'bg-kam-green-soft text-kam-green border-kam-green/25'
              : 'bg-kam-red-soft text-kam-red border-kam-red/25',
          )}>{f.side}</span>
          <div className="flex-1 min-w-0">
            <div className="text-sm truncate"><span className="font-mono">{f.code}</span> · {f.name}</div>
            <div className="text-[11px] text-text-faint font-mono">
              {f.filledQty}/{f.requestedQty} {f.avgFillPrice != null ? '@ ' + num(f.avgFillPrice) : ''}
            </div>
          </div>
          <StatusChip status={f.status} />
        </div>
      ))}
    </Section>
  );
}

// ─── Pending orders ──────────────────────────────────────────────────
function PendingCard({ pending, loading }: { pending: PendingOrder[]; loading: boolean }) {
  return (
    <Section title="Pending orders" subtitle={`${pending.length} open`}>
      {loading && <Empty>Loading…</Empty>}
      {!loading && pending.length === 0 && <Empty>Nothing pending.</Empty>}
      {pending.map((p) => (
        <div key={p.requestId} className="row flex items-center gap-3 py-2.5 border-b border-[color:var(--border)] last:border-0">
          <Clock size={14} className={cn(
            'flex-none',
            p.ageSeconds > 600 ? 'text-kam-amber' : 'text-text-faint',
          )} />
          <span className={cn(
            'inline-flex items-center px-1.5 py-0.5 rounded-chip text-[9px] font-bold border',
            p.side === 'BUY'
              ? 'bg-kam-green-soft text-kam-green border-kam-green/25'
              : 'bg-kam-red-soft text-kam-red border-kam-red/25',
          )}>{p.side}</span>
          <div className="flex-1 min-w-0">
            <div className="text-sm truncate"><span className="font-mono">{p.code}</span> · {p.name}</div>
            <div className="text-[11px] text-text-faint font-mono">
              {p.requestedQty}sh {p.limitPrice != null ? '@ ' + num(p.limitPrice) : 'mkt'}
              {' · age '}{formatAge(p.ageSeconds)}
            </div>
          </div>
          <StatusChip status={p.status} />
        </div>
      ))}
    </Section>
  );
}

// ─── Agent decisions ticker ──────────────────────────────────────────
function DecisionsCard({ decisions, loading }: { decisions: AgentDecision[]; loading: boolean }) {
  return (
    <Section title="Agent decisions" subtitle={`${decisions.length} recent`} viewAllHref="/agents">
      {loading && <Empty>Loading…</Empty>}
      {!loading && decisions.length === 0 && <Empty>No agent activity in last 48h.</Empty>}
      {decisions.map((d) => (
        <div key={d.logId} className="flex items-start gap-3 py-2.5 border-b border-[color:var(--border)] last:border-0">
          <AgentAvatar agent={d.agent} size="sm" />
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1.5 text-[11px] text-text-faint mb-0.5">
              <span className="font-mono">{timeOnly(d.at)}</span>
              <span>·</span>
              <span className="uppercase tracking-wider font-semibold">{displayAgent(d.agent)}</span>
              {!d.success && <span className="text-kam-red font-semibold">· ERROR</span>}
              {d.latencyMs != null && <span>· {d.latencyMs}ms</span>}
            </div>
            <div className={cn(
              'text-xs leading-relaxed line-clamp-2',
              d.success ? 'text-text-dim' : 'text-kam-red',
            )}>
              {d.summary ?? '(no summary)'}
            </div>
          </div>
        </div>
      ))}
    </Section>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────
function Section({
  title, subtitle, viewAllHref, children,
}: { title: string; subtitle?: string; viewAllHref?: string; children: React.ReactNode }) {
  return (
    <div className="card !p-0 overflow-hidden flex flex-col">
      <div className="px-4 py-3 flex items-center justify-between border-b border-[color:var(--border)]">
        <div>
          <h2 className="text-sm font-semibold">{title}</h2>
          {subtitle && <div className="text-[11px] text-text-faint">{subtitle}</div>}
        </div>
        {viewAllHref && (
          <Link to={viewAllHref} className="text-[11px] text-text-dim hover:text-text inline-flex items-center gap-1">
            view all <ExternalLink size={11} />
          </Link>
        )}
      </div>
      <div className="px-4 pb-1">{children}</div>
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="py-6 text-sm text-text-faint text-center">{children}</div>;
}

function StatusChip({ status }: { status: string }) {
  const tone =
    status === 'FILLED' ? 'bg-kam-green-soft text-kam-green border-kam-green/25' :
    status === 'PARTIALLY_FILLED' ? 'bg-kam-amber-soft text-kam-amber border-kam-amber/30' :
    status === 'SUBMITTED' ? 'bg-kam-cyan-soft text-kam-cyan border-kam-cyan/30' :
    'bg-surface-3 text-text-dim border-[color:var(--border)]';
  return (
    <span className={cn(
      'inline-flex items-center px-1.5 py-0.5 rounded-chip text-[9px] font-bold border whitespace-nowrap',
      tone,
    )}>
      {status.replace('_', ' ')}
    </span>
  );
}

function formatAge(s: number): string {
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  return `${Math.floor(s / 3600)}h${Math.floor((s % 3600) / 60)}m`;
}

